import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiConstants } from "../../constants/ApiConstants";
import { encodeID, parseApiDateTime, timeAgo } from "../../utils/Helper";
import { PAGINATION } from "../../constants/Constants";
import { LanguageConstants } from "../../constants/LanguageConstants";
import WelcomeMessage from "../../Modal/WelcomeMessage";
import packageIcon from "../../Assets/images/Package.svg";
import fileIcon from "../../Assets/images/File.svg";
import profileIcon from "../../Assets/images/Icon.svg";
import welcomeLogoIcon from "../../Assets/images/logoNotification.png";
import { Config } from "../../config/Config";
import { useDispatch, useSelector } from "react-redux";
import { ProfileAction } from "../../redux/Action/Authaction";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { isBusinessProfileIncomplete } from "../../utils/pendingDashboardActions";
import {
  formatNotificationBody,
  formatNotificationTitle,
} from "../../utils/notificationMessageDisplay";

const SYNTHETIC_COMPLETE_PROFILE_ID = "__qn_complete_profile_reminder__";

function parseUserBioMeta(rawBio: unknown): Record<string, unknown> {
  if (rawBio == null) return {};
  if (typeof rawBio === "object") return rawBio as Record<string, unknown>;
  try {
    const parsed = JSON.parse(String(rawBio));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function isProfileReminderReadInBio(user: unknown): boolean {
  const bio = parseUserBioMeta((user as { bio?: unknown })?.bio);
  const flag = bio.profile_reminder_notification_read;
  if (flag === true || flag === 1 || String(flag).toLowerCase() === "true") return true;
  return Boolean(String(bio.profile_reminder_read_at ?? "").trim());
}

const NOTIF_QUOTE_PNG = `${Config.BASE_URL}/Quote.png`;
const NOTIF_ORDER_PNG = `${Config.BASE_URL}/order.png`;

export type NotificationPanelProps = {
  /** When false, skip list fetch (popup closed). */
  isActive?: boolean;
  onClose?: () => void;
};

export function NotificationPanel({ isActive = true, onClose }: NotificationPanelProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const profiledata = useSelector((state: any) => state?.ProfileState);
  const notification = useMakeRequest();
  const markRead = useMakeRequest();
  const deleteNotification = useMakeRequest();
  const deleteAllNotifications = useMakeRequest();
  const [showWelcome, setShowWelcome] = useState(false);
  const [pagination, setPagination] = useState<any>({ pageIndex: PAGINATION?.PAGE, pageSize: PAGINATION?.PAGESIZE });
  const [activeFilter, setActiveFilter] = useState<"today" | "week" | "earlier" | "all">("today");
  const [notificationRows, setNotificationRows] = useState<any[]>([]);
  const [openWelcomeAfterReload, setOpenWelcomeAfterReload] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteAllRequested, setDeleteAllRequested] = useState(false);
  const [syntheticProfileReminderRead, setSyntheticProfileReminderRead] = useState(false);
  /** Bumps every 30s so relative timestamps stay current while the panel is open. */
  const [relativeTimeTick, setRelativeTimeTick] = useState(0);
  const listFetching =
    notification.loading && !openWelcomeAfterReload && !showWelcome;

  /** Refs keep list fetch stable — `useMakeRequest` returns a new object every render; putting it in useCallback deps caused infinite refetch. */
  const notificationReqRef = useRef(notification.makeRequest);
  notificationReqRef.current = notification.makeRequest;
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const reloadNotificationList = useCallback(() => {
    const p = paginationRef.current;
    notificationReqRef.current({
      payload: { page: p.pageIndex, pageSize: p.pageSize },
      url: ApiConstants?.NotificationList,
      toastStatus: false,
    });
  }, []);

  useEffect(() => {
    const user = profiledata?.data?.data;
    if (user && isProfileReminderReadInBio(user)) {
      setSyntheticProfileReminderRead(true);
    }
  }, [profiledata?.data?.data]);

  useEffect(() => {
    if (!isActive) return;
    reloadNotificationList();
  }, [isActive, pagination.pageIndex, pagination.pageSize, reloadNotificationList]);

  useEffect(() => {
    if (!isActive) return;
    const tickId = window.setInterval(() => {
      setRelativeTimeTick((prev) => prev + 1);
    }, 30_000);
    return () => window.clearInterval(tickId);
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    const refreshId = window.setInterval(() => {
      reloadNotificationList();
    }, 60_000);
    return () => window.clearInterval(refreshId);
  }, [isActive, reloadNotificationList]);

  useEffect(() => {
    const onRefresh = () => {
      if (isActive) reloadNotificationList();
    };
    window.addEventListener("notification:refresh", onRefresh);
    return () => window.removeEventListener("notification:refresh", onRefresh);
  }, [isActive, reloadNotificationList]);

  const handleIncreasePageSize = () => {
    setPagination((prevState: any) => ({
      ...prevState,
      pageSize: prevState.pageSize + 10,
    }));
  };

  useEffect(() => {
    if (notification?.loading) return;
    const rows = notification?.data?.data?.rows || [];
    setNotificationRows(rows);
    if (notification?.status && openWelcomeAfterReload) {
      setShowWelcome(true);
      setOpenWelcomeAfterReload(false);
    }
  }, [notification?.status, notification?.loading, notification?.data, openWelcomeAfterReload]);

  useEffect(() => {
    if (!markRead?.status || !markRead?.data) return;
    const envelope = markRead.data as any;
    const inner = envelope?.data ?? envelope;
    const unread = Number(inner?.unread_count ?? inner?.unread_notification_count);
    if (Number.isFinite(unread)) {
      window.dispatchEvent(new CustomEvent("notification:read", { detail: { setUnreadTo: unread } }));
    }
    // @ts-ignore
    dispatch(ProfileAction());
    if (openWelcomeAfterReload) {
      reloadNotificationList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markRead?.status, markRead?.data, dispatch]);

  const isProfileReminderNotification = (row: any) =>
    Boolean(row?._syntheticProfileReminder) ||
    String(row?.id || "") === SYNTHETIC_COMPLETE_PROFILE_ID ||
    String(row?.data_params?.type || "").trim().toLowerCase() === "profile-reminder";

  const isWelcomeNotification = (row: any) => {
    const notificationTitle = String(row?.notification_params?.title || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const notificationType = row?.data_params?.type;
    const isWelcomeTitle =
      notificationTitle === "welcome to quote n buy" ||
      notificationTitle === "bienvenido a quote n buy";
    return (
      isWelcomeTitle &&
      (notificationType === "Admin" || notificationType === "admin-send")
    );
  };

  const isSingleLineNotification = (row: any) => isWelcomeNotification(row);

  const isNotificationUnread = (row: any) =>
    String(row?.is_read) === "0" || row?.is_read === 0 || row?.is_read === false;

  useEffect(() => {
    if (deleteNotification?.status && deleteTarget) {
      setNotificationRows((prevRows: any[]) => prevRows.filter((item: any) => item?.id !== deleteTarget?.id));
      // @ts-ignore
      dispatch(ProfileAction());
      reloadNotificationList();
      setDeleteTarget(null);
    }
  }, [deleteNotification?.status, deleteTarget, dispatch, reloadNotificationList]);

  useEffect(() => {
    if (deleteAllNotifications?.status && deleteAllRequested) {
      setNotificationRows([]);
      window.dispatchEvent(new CustomEvent("notification:read", { detail: { setUnreadTo: 0 } }));
      // @ts-ignore
      dispatch(ProfileAction());
      reloadNotificationList();
      setDeleteAllRequested(false);
    }
  }, [deleteAllNotifications?.status, deleteAllRequested, dispatch, reloadNotificationList]);

  const closeNotificationsUi = () => {
    onClose?.();
  };

  const handelNavigate = (data: any) => {
    const shouldOpenWelcomePopup = isWelcomeNotification(data);
    if (isNotificationUnread(data)) {
      window.dispatchEvent(new CustomEvent("notification:read", { detail: { decrementBy: 1 } }));
    }
    setNotificationRows((prevRows: any[]) =>
      prevRows.map((item: any) => {
        if (item?.id === data?.id) return { ...item, is_read: "1" };
        if (isProfileReminderNotification(data) && isProfileReminderNotification(item)) {
          return { ...item, is_read: "1" };
        }
        return item;
      }),
    );

    const payload = {
      notification_id: data?.id,
    };
    setShowWelcome(false);

    if (isProfileReminderNotification(data)) {
      closeNotificationsUi();
      setSyntheticProfileReminderRead(true);
      markRead.makeRequest({ payload, url: ApiConstants?.NotificationMarkRead, toastStatus: false });
      navigate("/profile");
      return;
    }

    setOpenWelcomeAfterReload(shouldOpenWelcomePopup);
    markRead.makeRequest({ payload, url: ApiConstants?.NotificationMarkRead, toastStatus: false });

    if (shouldOpenWelcomePopup) {
      return;
    }

    closeNotificationsUi();

    const typeNorm = String(data?.data_params?.type || "").trim().toLowerCase();
    const refId = data?.data_params?.id;
    const refIdStr = refId != null && refId !== "" ? String(refId).trim() : "";

    if ((typeNorm === "quotation" || typeNorm === "quote") && refIdStr) {
      navigate(`/view-quotation/${refIdStr}`);
      return;
    }
    if (typeNorm === "order" && refIdStr) {
      navigate(`/order-detail/${encodeID(refIdStr)}`);
    }
  };

  const getNotificationIcon = (data: any) => {
    if (isWelcomeNotification(data)) {
      return {
        src: welcomeLogoIcon,
        className: "notification-icon is-welcome",
      };
    }
    if (isProfileReminderNotification(data)) {
      return {
        src: profileIcon,
        className: "notification-icon is-profile",
      };
    }
    const dpType = String(data?.data_params?.type || "").trim().toLowerCase();
    if (dpType === "quotation" || dpType === "quote") {
      return {
        src: NOTIF_QUOTE_PNG,
        className: "notification-icon is-quote",
      };
    }
    if (dpType === "order") {
      return {
        src: NOTIF_ORDER_PNG,
        className: "notification-icon is-order",
      };
    }
    const title = String(data?.notification_params?.title || "").trim().toLowerCase();
    const message = String(data?.notification_params?.body || "").trim().toLowerCase();
    const type = String(data?.data_params?.type || "").trim().toLowerCase();
    const extraDetails = String(JSON.stringify(data?.data_params || {})).toLowerCase();
    const normalizedText = `${title} ${message} ${type} ${extraDetails}`;

    if (/order|quotation|quote|product|package|shipment|shipping|delivery|tracking|awb|waybill|cart|purchase/.test(normalizedText)) {
      return {
        src: packageIcon,
        className: "notification-icon is-package",
      };
    }

    if (/file|document|pdf|excel|upload|attachment|template|bulk/.test(normalizedText)) {
      return {
        src: fileIcon,
        className: "notification-icon is-file",
      };
    }

    return {
      src: profileIcon,
      className: "notification-icon is-profile",
    };
  };

  const handleDeleteNotification = (data: any, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isProfileReminderNotification(data)) return;
    Swal.fire({
      icon: "warning",
      title: LanguageConstants?.ARE_YOU_SURE,
      text: LanguageConstants?.YOU_WANT_TO_DELETE_NOTIFICATION,
      showCancelButton: true,
      cancelButtonText: LanguageConstants?.NO,
      confirmButtonText: LanguageConstants?.YES,
      showCloseButton: true,
      buttonsStyling: false,
      reverseButtons: true,
      customClass: {
        container: "logout-com-modal-wrap",
        popup: "logout-com-modal",
        title: "logout-com-modal__title",
        closeButton: "logout-com-modal__close",
        htmlContainer: "logout-com-modal__html",
        actions: "logout-com-modal__footer",
        confirmButton: "logout-com-modal__btn logout-com-modal__btn--confirm com-btn",
        cancelButton: "logout-com-modal__btn logout-com-modal__btn--cancel com-btn com-btn-outline",
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        if (isNotificationUnread(data)) {
          window.dispatchEvent(new CustomEvent("notification:read", { detail: { decrementBy: 1 } }));
        }
        const payload = {
          detail_id: data?.id,
        };
        setDeleteTarget(data);
        deleteNotification
          .makeRequest({
            payload,
            url: ApiConstants?.NotificationDelete,
            toastStatus: true,
          })
          .catch(() => {
            setDeleteTarget(null);
          });
      }
    });
  };

  const handleDeleteAllNotifications = () => {
    Swal.fire({
      icon: "warning",
      title: LanguageConstants?.ARE_YOU_SURE,
      text: LanguageConstants?.YOU_WANT_TO_DELETE_ALL_NOTIFICATION,
      showCancelButton: true,
      cancelButtonText: LanguageConstants?.NO,
      confirmButtonText: LanguageConstants?.YES,
      showCloseButton: true,
      buttonsStyling: false,
      reverseButtons: true,
      customClass: {
        container: "logout-com-modal-wrap",
        popup: "logout-com-modal",
        title: "logout-com-modal__title",
        closeButton: "logout-com-modal__close",
        htmlContainer: "logout-com-modal__html",
        actions: "logout-com-modal__footer",
        confirmButton: "logout-com-modal__btn logout-com-modal__btn--confirm com-btn",
        cancelButton: "logout-com-modal__btn logout-com-modal__btn--cancel com-btn com-btn-outline",
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        const payload = {
          user_id: profiledata?.data?.data?.id,
        };
        setDeleteAllRequested(true);
        deleteAllNotifications
          .makeRequest({
            payload,
            url: ApiConstants?.NotificationDeleteAll,
            toastStatus: true,
          })
          .catch(() => {
            setDeleteAllRequested(false);
          });
      }
    });
  };

  const profileCompleteReminderRow = useMemo(() => {
    const user = profiledata?.data?.data;
    const alreadyExistsInApi = notificationRows.some((row: any) => isProfileReminderNotification(row));
    if (alreadyExistsInApi) return null;
    if (!isBusinessProfileIncomplete(user)) return null;
    return {
      id: SYNTHETIC_COMPLETE_PROFILE_ID,
      is_read: syntheticProfileReminderRead ? "1" : "0",
      created_at: new Date().toISOString(),
      _syntheticProfileReminder: true,
      notification_params: {
        title: LanguageConstants?.COMPLETE_PROFILE_NOTIF_TITLE ?? "Complete your profile",
        body: LanguageConstants?.COMPLETE_PROFILE_NOTIF_BODY ?? "",
      },
      data_params: { type: "profile-reminder" },
    };
  }, [profiledata?.data?.data, notificationRows, syntheticProfileReminderRead]);

  const apiRowsFilteredByDate = useMemo(() => {
    const getCreatedAt = (item: any) => parseApiDateTime(item?.created_at)?.toDate() ?? new Date(NaN);
    if (activeFilter === "all") return notificationRows;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfRollingWeek = new Date(startOfToday);
    startOfRollingWeek.setDate(startOfRollingWeek.getDate() - 7);
    const twoMonthsAgo = new Date(startOfToday);
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

    if (activeFilter === "today") {
      return notificationRows.filter((item: any) => {
        const createdAt = getCreatedAt(item);
        return !Number.isNaN(createdAt.getTime()) && createdAt >= startOfToday;
      });
    }

    if (activeFilter === "week") {
      return notificationRows.filter((item: any) => {
        const createdAt = getCreatedAt(item);
        return (
          !Number.isNaN(createdAt.getTime()) &&
          createdAt >= startOfRollingWeek &&
          createdAt < startOfToday
        );
      });
    }

    return notificationRows.filter((item: any) => {
      const createdAt = getCreatedAt(item);
      return (
        !Number.isNaN(createdAt.getTime()) &&
        createdAt >= twoMonthsAgo &&
        createdAt < startOfRollingWeek
      );
    });
  }, [activeFilter, notificationRows]);

  const filteredNotifications = useMemo(() => {
    if (!profileCompleteReminderRow) return apiRowsFilteredByDate;
    return [profileCompleteReminderRow, ...apiRowsFilteredByDate];
  }, [profileCompleteReminderRow, apiRowsFilteredByDate]);

  return (
    <>
      <div
        className={`notification-panel${showWelcome ? " is-blurred" : ""}${deleteAllNotifications.loading ? " is-busy" : ""}`}
      >
        <header className="notification-panel__header" aria-label="Notifications header">
          <div className="notification-panel__title-block">
            <h2 className="notification-heading" id="notification-popup-title">
              {LanguageConstants?.NOTIFACTION}
            </h2>
          </div>
          <div className="notification-panel__segmented" role="tablist" aria-label="Notification time range">
            <div className="notification-tabs">
              <button
                type="button"
                role="tab"
                aria-selected={activeFilter === "today"}
                className={activeFilter === "today" ? "active" : ""}
                onClick={() => setActiveFilter("today")}
              >
                {LanguageConstants?.NOTIF_TODAY}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeFilter === "week"}
                className={activeFilter === "week" ? "active" : ""}
                onClick={() => setActiveFilter("week")}
              >
                {LanguageConstants?.NOTIF_THIS_WEEK}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeFilter === "earlier"}
                className={activeFilter === "earlier" ? "active" : ""}
                onClick={() => setActiveFilter("earlier")}
              >
                {LanguageConstants?.NOTIF_EARLIER}
              </button>
            </div>
          </div>
          <div className="notification-panel__toolbar">
            {activeFilter !== "all" ? (
              <button className="notification-see-all" type="button" onClick={() => setActiveFilter("all")}>
                {LanguageConstants?.NOTIF_SEE_ALL}
              </button>
            ) : null}
            {activeFilter === "all" ? (
              <button
                className="notification-see-all del"
                type="button"
                aria-label={LanguageConstants?.DELETE_ALL}
                title={LanguageConstants?.DELETE_ALL}
                disabled={deleteAllNotifications.loading || notificationRows.length === 0}
                onClick={handleDeleteAllNotifications}
              >
                Delete All
              </button>
            ) : null}
            {onClose ? (
              <button
                type="button"
                className="notification-close-btn"
                aria-label={LanguageConstants?.ARIA_CLOSE ?? "Close"}
                onClick={onClose}
              >
                <i className="icon-close" aria-hidden />
              </button>
            ) : null}
          </div>
        </header>

        <div className="notification-list refreshed">
          {listFetching && filteredNotifications.length === 0 ? (
            <div className="notification-panel__list-loading" aria-live="polite" aria-busy="true">
              <div className="spinner-border text-primary notification-panel__spinner" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : filteredNotifications.length > 0 ? (
            filteredNotifications.map((res: any, i: any) => {
              const message = formatNotificationBody(res);
              const title = formatNotificationTitle(res);
              const iconData = getNotificationIcon(res);
              const singleLine = isSingleLineNotification(res);
              const suppressUnreadDot = singleLine;
              const showMessageLine = !singleLine && String(message ?? "").trim() !== "";
              const isRead =
                res?.is_read === true || res?.is_read === 1 || String(res?.is_read) === "1";

              return (
                <div
                  className={`notification-single${isRead ? " is-read" : " is-unread"}${singleLine ? " notification-single--single-line" : ""}`}
                  key={res?.id || i}
                  onClick={() => handelNavigate(res)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="notification-icon-wrap">
                    <img className={iconData.className} src={iconData.src} alt="" />
                  </div>
                  <div className="notification-content">
                    <div className="notification-row-title">
                      {isRead || suppressUnreadDot ? null : <span className="dot" />}
                      <div
                        className={`notification-text-wrap${singleLine ? " notification-text-wrap--single" : ""}`}
                      >
                        <p>{title}</p>
                        {showMessageLine ? <p className="notification-text-wrap__body">{message}</p> : null}
                      </div>
                    </div>
                  </div>
                  <div className="notification-meta">
                    <div className="notification-time" key={relativeTimeTick}>
                      {timeAgo(res?.created_at)}
                    </div>
                    {isProfileReminderNotification(res) ? null : (
                      <button
                        type="button"
                        className="notification-delete-btn"
                        aria-label="Delete notification"
                        disabled={deleteNotification.loading && deleteTarget?.id === res?.id}
                        onClick={(event) => handleDeleteNotification(res, event)}
                      >
                        <i className="icon-delete-2" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center mt-4 mb-0">{LanguageConstants?.DATANOTFOUND}</p>
          )}
        </div>
        {notification?.data?.data?.count > 10 && notification?.data?.data?.count > pagination.pageSize ? (
          <div className="notification-panel__more-wrap">
            <button type="button" className="round-btn" onClick={() => handleIncreasePageSize()}>
              {LanguageConstants?.MORE}
            </button>
          </div>
        ) : null}
      </div>
      <Modal
        show={showWelcome}
        onHide={() => setShowWelcome(false)}
        centered
        className="auth-modal text-center"
        dialogClassName="modal-dialog-centered"
      >
        <WelcomeMessage setShow={setShowWelcome} />
      </Modal>
    </>
  );
}
